import {
  SYSTEM_STRUCTURE_DEFINITIONS,
  SYSTEM_STRUCTURE_VERSION,
} from './systemStructure.constants';
import {
  normalizeStructureKey,
  sortByOrderThenName,
  toStructureKey,
} from './systemStructure.utils';

const getSystemName = (entry) => entry?.system_type || entry?.name || '';
const getDeviceTypeName = (entry) => entry?.device_type || entry?.name || '';
const normalizeCategoryLookupKey = (value = '') =>
  String(value)
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const CATEGORY_TYPE_LOOKUP = SYSTEM_STRUCTURE_DEFINITIONS.reduce((lookup, category) => {
  lookup[normalizeCategoryLookupKey(category.id)] = category.id;
  lookup[normalizeCategoryLookupKey(category.name)] = category.id;
  return lookup;
}, {});

export const DEVICE_TYPE_ENDPOINT_EXPECTATION = {
  endpoint: '/v2/integrations/device/map',
  requiredCategoryField: 'category_type',
  expectedShape: {
    system_type: 'string',
    device_type: 'string',
    category_type: 'string',
  },
};

const getDeviceTypeEntries = (deviceTypeMap) => {
  if (!deviceTypeMap) return [];

  if (Array.isArray(deviceTypeMap)) {
    return deviceTypeMap
      .map((entry) => ({
        ...entry,
        system_type: getSystemName(entry),
        device_type: getDeviceTypeName(entry),
      }))
      .filter((entry) => entry.system_type && entry.device_type);
  }

  if (typeof deviceTypeMap !== 'object') return [];

  return Object.entries(deviceTypeMap).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((deviceType) => ({
        system_type: key,
        device_type: deviceType,
      }));
    }

    if (value?.system_type) {
      return [
        {
          ...value,
          system_type: value.system_type,
          device_type: value.device_type || key,
        },
      ];
    }

    return [];
  });
};

const getSystemTypeEntries = (systemTypeMap) => {
  if (!systemTypeMap) return [];

  const entries = Array.isArray(systemTypeMap) ? systemTypeMap : Object.values(systemTypeMap);

  return entries
    .map((entry) => ({
      ...entry,
      system_type: getSystemName(entry),
    }))
    .filter((entry) => entry.system_type);
};

const getDefinitionForDeviceType = (systemType, deviceType) => {
  const normalizedSystem = normalizeStructureKey(systemType);
  const normalizedDeviceType = normalizeStructureKey(deviceType);

  for (const category of SYSTEM_STRUCTURE_DEFINITIONS) {
    for (const type of category.types || []) {
      const match = (type.deviceTypes || []).find(
        (definition) =>
          normalizeStructureKey(definition.system_type) === normalizedSystem &&
          normalizeStructureKey(definition.device_type) === normalizedDeviceType,
      );

      if (match) return { category, type, deviceType: match };
    }
  }

  return null;
};

const createSystemTypeMeta = (entry) => ({
  id: entry.id ?? entry.system_type_id,
  key: toStructureKey(entry.key || entry.system_type),
  name: entry.system_type,
  display_name: entry.display_name || entry.system_type,
  connectable: entry.connectable ?? true,
  source: entry,
  device_types: [],
});

const createFallbackSystemTypeMeta = (entry) => ({
  id: entry.system_type_id,
  key: toStructureKey(entry.system_type),
  name: entry.system_type,
  display_name: entry.system_type,
  connectable: true,
  source: { system_type: entry.system_type },
  device_types: [],
});

const createDeviceTypeMeta = (entry, system) => {
  const definition = getDefinitionForDeviceType(entry.system_type, entry.device_type);
  const endpointCategoryType = entry.category_type
    ? CATEGORY_TYPE_LOOKUP[normalizeCategoryLookupKey(entry.category_type)] || entry.category_type
    : undefined;
  const categoryType = endpointCategoryType || definition?.category?.id;

  return {
    id: entry.id ?? entry.device_type_id,
    key: toStructureKey(entry.key || entry.device_type),
    name: entry.device_type,
    display_name: entry.display_name || entry.device_type,
    category_type: categoryType,
    system_display_name: definition?.deviceType?.system_display_name,
    system_type: entry.system_type,
    system_type_id: entry.system_type_id ?? system.id,
    structure_category_id: categoryType,
    structure_type_id: definition?.type?.id,
    sort_order: definition?.deviceType?.sort_order ?? entry.sort_order ?? 999,
    source: entry,
  };
};

const getDeviceTypeLookupKey = (systemType, deviceType) =>
  `${normalizeStructureKey(systemType)}::${normalizeStructureKey(deviceType)}`;

const logDeviceTypeCategoryEndpointStatus = (
  deviceTypeEntries,
  deviceTypesByKey,
  deviceTypeEndpointExpectation,
) => {
  if (!deviceTypeEntries.length) return;

  const endpointEntriesWithCategory = deviceTypeEntries.filter((entry) => entry.category_type);
  const categoriesInUse = Array.from(deviceTypesByKey.values()).map((deviceType) => ({
    system_type: deviceType.system_type,
    device_type: deviceType.name,
    endpoint_category_type: deviceType.source?.category_type,
    category_type_in_use: deviceType.category_type,
    using_fallback: !deviceType.source?.category_type && !!deviceType.category_type,
  }));

  // console.info('[SystemStructure] Device type category endpoint status', {
  //   expectedEndpoint: deviceTypeEndpointExpectation,
  //   endpointHasCategoryType: endpointEntriesWithCategory.length > 0,
  //   status:
  //     endpointEntriesWithCategory.length > 0
  //       ? 'using endpoint category_type where provided'
  //       : 'using frontend fallback category metadata',
  //   categoriesInUse,
  // });
};

const buildCategories = (deviceTypesByKey) =>
  SYSTEM_STRUCTURE_DEFINITIONS.map((categoryDefinition) => ({
    id: categoryDefinition.id,
    name: categoryDefinition.name,
    sort_order: categoryDefinition.sort_order,
    types: (categoryDefinition.types || []).map((typeDefinition) => ({
      id: typeDefinition.id,
      category_id: categoryDefinition.id,
      name: typeDefinition.name,
      sort_order: typeDefinition.sort_order,
      device_types: (typeDefinition.deviceTypes || [])
        .map((deviceTypeDefinition) => {
          const deviceType = deviceTypesByKey.get(
            getDeviceTypeLookupKey(
              deviceTypeDefinition.system_type,
              deviceTypeDefinition.device_type,
            ),
          );
          if (!deviceType) return null;

          return {
            ...deviceType,
            system_display_name:
              deviceType.system_display_name || deviceTypeDefinition.system_display_name,
          };
        })
        .filter(Boolean)
        .sort(sortByOrderThenName),
    })),
  }))
    .map((category) => ({
      ...category,
      types: category.types
        .filter((type) => type.device_types.length > 0)
        .sort(sortByOrderThenName),
    }))
    .filter((category) => category.types.length > 0)
    .sort(sortByOrderThenName);

const appendUncategorizedDeviceTypes = (categories, deviceTypesByKey) => {
  const uncategorizedDeviceTypes = Array.from(deviceTypesByKey.values()).filter(
    (deviceType) => !deviceType.structure_category_id,
  );

  if (uncategorizedDeviceTypes.length === 0) return categories;

  return [
    ...categories,
    {
      id: 'uncategorized',
      name: 'Uncategorized',
      sort_order: 999,
      types: [
        {
          id: 'uncategorized-device-types',
          category_id: 'uncategorized',
          name: 'Other',
          sort_order: 999,
          device_types: uncategorizedDeviceTypes.sort(sortByOrderThenName),
        },
      ],
    },
  ];
};

export const createSystemStructure = ({
  systemTypeMap,
  deviceTypeMap,
  deviceTypeEndpointExpectation = DEVICE_TYPE_ENDPOINT_EXPECTATION,
} = {}) => {
  const systemTypesByName = new Map();
  const deviceTypesByKey = new Map();
  const deviceTypeEntries = getDeviceTypeEntries(deviceTypeMap);

  getSystemTypeEntries(systemTypeMap).forEach((entry) => {
    systemTypesByName.set(normalizeStructureKey(entry.system_type), createSystemTypeMeta(entry));
  });

  deviceTypeEntries.forEach((entry) => {
    const systemKey = normalizeStructureKey(entry.system_type);
    const system =
      systemTypesByName.get(systemKey) ||
      systemTypesByName.set(systemKey, createFallbackSystemTypeMeta(entry)).get(systemKey);
    const deviceType = createDeviceTypeMeta(entry, system);

    deviceTypesByKey.set(getDeviceTypeLookupKey(entry.system_type, entry.device_type), deviceType);
    system.device_types.push(deviceType);
  });

  logDeviceTypeCategoryEndpointStatus(
    deviceTypeEntries,
    deviceTypesByKey,
    deviceTypeEndpointExpectation,
  );

  const categories = appendUncategorizedDeviceTypes(
    buildCategories(deviceTypesByKey),
    deviceTypesByKey,
  );

  return {
    version: SYSTEM_STRUCTURE_VERSION,
    categories,
    systemTypes: Array.from(systemTypesByName.values()).map((systemType) => ({
      ...systemType,
      device_types: systemType.device_types.sort(sortByOrderThenName),
    })),
    deviceTypes: Array.from(deviceTypesByKey.values()).sort(sortByOrderThenName),
    systemTypesByName,
    deviceTypesByKey,
  };
};
